import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../../../context/DarkModeContext';
import { IoSparklesOutline, IoCloseOutline, IoInformationCircleOutline } from 'react-icons/io5';
import { Achievement } from '../../../hooks/useAchievements';

interface STARBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (achievement: Omit<Achievement, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'focus_id'>) => Promise<void>;
  focusId: string;
  existingAchievement?: Achievement;
}

const CATEGORIES = [
  { value: 'technical', label: '💻 Technical Excellence' },
  { value: 'leadership', label: '👥 Leadership' },
  { value: 'collaboration', label: '🤝 Collaboration' },
  { value: 'innovation', label: '💡 Innovation' },
  { value: 'impact', label: '🎯 Business Impact' },
  { value: 'growth', label: '📈 Personal Growth' }
];

const STAR_PROMPTS = {
  situation: [
    "What was the context or background?",
    "What challenge were you facing?",
    "What was the state of things before you got involved?"
  ],
  task: [
    "What was your specific responsibility?",
    "What were you asked to do?",
    "What goals were you trying to achieve?"
  ],
  action: [
    "What specific steps did you take?",
    "How did you approach the problem?",
    "What tools or methods did you use?"
  ],
  result: [
    "What was the outcome?",
    "What impact did your actions have?",
    "What metrics improved?"
  ]
};

const STARBuilder: React.FC<STARBuilderProps> = ({
  isOpen,
  onClose,
  onSave,
  focusId,
  existingAchievement
}) => {
  const { darkMode } = useDarkMode();
  const [formData, setFormData] = useState({
    title: existingAchievement?.title || '',
    situation: existingAchievement?.situation || '',
    task: existingAchievement?.task || '',
    action: existingAchievement?.action || '',
    result: existingAchievement?.result || '',
    achievement_date: existingAchievement?.achievement_date || new Date().toISOString().split('T')[0],
    category: existingAchievement?.category || 'technical',
    impact_score: existingAchievement?.impact_score || 3
  });
  const [activeSection, setActiveSection] = useState<'situation' | 'task' | 'action' | 'result'>('situation');
  const [isSaving, setIsSaving] = useState(false);
  const [showAIAssist, setShowAIAssist] = useState(false);

  // Style classes
  const bgColor = darkMode ? 'bg-gray-900' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-gray-50';
  const inputBg = darkMode ? 'bg-gray-700' : 'bg-white';

  const handleSubmit = async () => {
    // Validate all fields are filled
    if (!formData.title || !formData.situation || !formData.task || !formData.action || !formData.result) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave({
        ...formData,
        focus_id: focusId,
        is_featured: existingAchievement?.is_featured || false
      });
      onClose();
    } catch (error) {
      console.error('Failed to save achievement:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const sections: Array<'situation' | 'task' | 'action' | 'result'> = ['situation', 'task', 'action', 'result'];

  const getSectionLabel = (section: string) => {
    return section.charAt(0).toUpperCase() + section.slice(1);
  };

  const getCompletionPercentage = () => {
    const requiredFields = ['title', 'situation', 'task', 'action', 'result'];
    const completedFields = requiredFields.filter(field => formData[field as keyof typeof formData]);
    return (completedFields.length / requiredFields.length) * 100;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/50" />

        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className={`relative w-full max-w-4xl max-h-[90vh] ${bgColor} rounded-xl shadow-2xl overflow-hidden`}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-6 border-b ${borderColor}`}>
            <div>
              <h2 className={`text-xl font-semibold ${textColor}`}>
                {existingAchievement ? 'Edit Achievement' : 'Create Achievement Story'}
              </h2>
              <p className={`text-sm ${mutedTextColor} mt-1`}>
                Use the STAR method to structure your achievement
              </p>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
            >
              <IoCloseOutline className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm ${mutedTextColor}`}>Completion</span>
              <span className={`text-sm font-medium ${textColor}`}>
                {Math.round(getCompletionPercentage())}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <motion.div
                className="bg-purple-600 h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${getCompletionPercentage()}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-240px)]">
            {/* Title and Metadata */}
            <div className="mb-6">
              <label className={`block text-sm font-medium ${textColor} mb-2`}>
                Achievement Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Led Migration to Microservices Architecture"
                className={`w-full px-4 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
              />

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className={`block text-sm font-medium ${textColor} mb-2`}>
                    Date
                  </label>
                  <input
                    type="date"
                    value={formData.achievement_date}
                    onChange={(e) => setFormData({ ...formData, achievement_date: e.target.value })}
                    className={`w-full px-4 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${textColor} mb-2`}>
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className={`w-full px-4 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* STAR Sections */}
            <div className="space-y-4">
              {sections.map((section) => (
                <motion.div
                  key={section}
                  className={`p-4 rounded-lg border ${borderColor} ${
                    activeSection === section ? 'ring-2 ring-purple-500' : ''
                  } ${cardBg} cursor-pointer`}
                  onClick={() => setActiveSection(section)}
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className={`text-lg font-medium ${textColor} capitalize`}>
                        {getSectionLabel(section)}
                      </h3>
                      <div className={`text-sm ${mutedTextColor} mt-1 space-y-1`}>
                        {STAR_PROMPTS[section].map((prompt, idx) => (
                          <p key={idx}>• {prompt}</p>
                        ))}
                      </div>
                    </div>
                    {formData[section] && (
                      <div className="ml-4">
                        <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <textarea
                    value={formData[section]}
                    onChange={(e) => setFormData({ ...formData, [section]: e.target.value })}
                    placeholder={`Describe the ${section}...`}
                    rows={3}
                    className={`w-full mt-3 px-4 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                  />
                </motion.div>
              ))}
            </div>

            {/* Impact Score */}
            <div className={`mt-6 p-4 rounded-lg ${cardBg} border ${borderColor}`}>
              <label className={`block text-sm font-medium ${textColor} mb-2`}>
                Impact Score
              </label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((score) => (
                  <button
                    key={score}
                    onClick={() => setFormData({ ...formData, impact_score: score })}
                    className={`px-4 py-2 rounded-lg border ${
                      formData.impact_score === score
                        ? 'bg-purple-600 text-white border-purple-600'
                        : `${borderColor} ${textColor} hover:bg-gray-100 dark:hover:bg-gray-700`
                    } transition-colors`}
                  >
                    {score}
                  </button>
                ))}
                <span className={`ml-4 text-sm ${mutedTextColor}`}>
                  {formData.impact_score <= 2 && 'Low Impact'}
                  {formData.impact_score === 3 && 'Moderate Impact'}
                  {formData.impact_score >= 4 && 'High Impact'}
                </span>
              </div>
            </div>

            {/* AI Assist Button */}
            <div className={`mt-6 p-4 rounded-lg ${cardBg} border ${borderColor}`}>
              <button
                onClick={() => setShowAIAssist(!showAIAssist)}
                className="flex items-center gap-2 text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
              >
                <IoSparklesOutline className="w-5 h-5" />
                <span>Get AI suggestions to improve your STAR story</span>
              </button>
              {showAIAssist && (
                <div className={`mt-3 p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800`}>
                  <p className={`text-sm ${mutedTextColor}`}>
                    AI assistance will be available in a future update. For now, follow the prompts to create compelling STAR stories.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className={`flex justify-between items-center p-6 border-t ${borderColor}`}>
            <div className="flex items-center gap-2">
              <IoInformationCircleOutline className={`w-5 h-5 ${mutedTextColor}`} />
              <span className={`text-sm ${mutedTextColor}`}>
                All fields are required for a complete STAR story
              </span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className={`px-6 py-2 rounded-lg border ${borderColor} ${textColor} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSaving || getCompletionPercentage() < 100}
                className={`px-6 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isSaving ? 'Saving...' : existingAchievement ? 'Update' : 'Save Achievement'}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default STARBuilder;